import { useEffect, useState, useMemo } from "react";
import { api, resolveAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { motion } from "framer-motion";
import { useSite } from "@/contexts/SiteContent";
import PhotoFrame from "@/components/PhotoFrame";
import RichText from "@/components/RichText";

const CATEGORIES = ["All", "Bank", "NBFC", "Fintech"];

export default function PartnersPage() {
  const { content } = useSite();
  const p = content?.partners_page || {};
  const hero = p.hero || {};
  const [partners, setPartners] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/partners").then(r => setPartners(r.data || [])).catch(()=>{}); }, []);
  const filtered = useMemo(() => partners.filter(p =>
    (cat === "All" || p.category === cat) && (q === "" || p.name.toLowerCase().includes(q.toLowerCase()))
  ), [partners, cat, q]);

  return (
    <div>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{opacity:0, y:14}} animate={{opacity:1, y:0}} transition={{duration:0.5}}>
            <span className="eyebrow-bar"><span className="overline">{hero.eyebrow || "Partner Network"}</span></span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 max-w-3xl text-navy">{hero.title}</h1>
            <RichText html={hero.subtitle} variant="subtitle" className="mt-6 max-w-3xl"/>
          </motion.div>
        </div>
        {hero.image_url && <div className="lg:col-span-5"><PhotoFrame src={hero.image_url} ratio="4/3"/></div>}
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-xs uppercase tracking-[0.22em] text-ink-muted mb-6">{p.marquee_heading || "Our lending partners"}</div>
          <div className="marquee-wrap overflow-hidden">
            <div className="marquee">
              {[...partners, ...partners].map((it, i) => (
                it.logo_url ? (
                  <img key={i} src={resolveAssetUrl(it.logo_url)} alt={it.name} className="h-10 w-auto opacity-70 hover:opacity-100 transition-opacity"/>
                ) : (
                  <div key={i} className="text-xl sm:text-2xl font-display font-semibold text-ink-muted hover:text-navy transition-colors whitespace-nowrap">{it.name}</div>
                )
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(c => (
              <Button key={c} variant="ghost" data-testid={`partner-filter-${c.toLowerCase()}`} onClick={() => setCat(c)}
                className={`rounded-full h-9 px-4 ${cat === c ? "bg-navy text-white hover:bg-navy hover:text-white" : "bg-white border border-slate-200 hover:bg-slate-100 text-navy"}`}>{c}</Button>
            ))}
          </div>
          <div className="relative sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted"/>
            <Input data-testid="partner-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search partners" className="pl-9 rounded-full"/>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="partners-grid">
          {filtered.map((pp, i) => (
            <motion.div key={pp.id} initial={{opacity:0, y:14}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.4, delay: (i % 8) * 0.05}}
              whileHover={{ y: -4 }} className="card-flat p-6 flex flex-col items-start gap-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-saffron">{pp.category}</div>
              {pp.logo_url ? (
                <img src={resolveAssetUrl(pp.logo_url)} alt={pp.name} className="h-12 w-auto object-contain"/>
              ) : (
                <div className="font-display font-extrabold text-lg sm:text-xl text-navy">{pp.name}</div>
              )}
              <div className="text-xs text-ink-muted">{pp.description}</div>
            </motion.div>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-center text-ink-muted py-10">No partners match your filters.</div>}
        </div>
      </section>
    </div>
  );
}
